import React from "react";
import { usePdpProduct } from "./PdpContext";
import {
  productSelect,
  variantSelect,
  DroppsProduct,
  droppsProduct,
} from "@/store";
import { shopifyCartApi, CartAddLineItem } from "@/services/shopifyCart";
import { useCartUiActions } from "../cart";
import { useQuery } from "@tanstack/react-query";
import { fetchJson } from "@/helpers/fetch";
import { storefrontApi } from "@/services/storefront";
import { scentMapping } from "@/helpers/scentMapping";

const shortenName = (title: string) =>
  title
    .split(",")[0]
    .replace(/laundry & household|pods|biobased|power/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();

const RecommendedProductCard = ({ product }: { product: DroppsProduct }) => {
  const shopifyCartAdder = shopifyCartApi.useTrackedAdd();
  const cartActions = useCartUiActions();
  const variant = productSelect.defaultVariant(product);
  const featuredImage = productSelect.featuredImage(product);
  const imageUrl = product.recommendedImage || featuredImage.url;
  const scent = product.scent?.name || productSelect.scent(product);
  const name = shortenName(product.title);
  const scentIconUrl =
    product.scent?.icon || (scent ? scentMapping[scent]?.src : undefined);
  const scentBgColor = scent ? scentMapping[scent]?.bgColor : undefined;

  const handleAdd = () => {
    const lineItem: CartAddLineItem[] = [
      variantSelect.cartAddLineItem(variant, {
        subscription: false,
        quantity: 1,
      }),
    ];
    shopifyCartAdder.mutate(
      { items: lineItem, sourceModule: "pdp_recommended_add" },
      { onSuccess: () => cartActions.openDrawer() }
    );
  };

  const size = variantSelect.sizeFormat(variant);
  const price = variant.price.format();

  return (
    <div className="flex flex-col items-center justify-between gap-4 rounded-[3px] border border-solid border-[#BACDFF] p-4 text-black md:flex-row md:gap-3 md:pl-0">
      <div className="flex w-full items-center md:w-auto">
        <img
          src={imageUrl}
          alt={featuredImage.alt || product.title}
          className="h-[80px] w-[100px] shrink-0 object-contain"
        />
        <div className="min-w-0">
          <a
            href={`/products/${product.handle}`}
            className="mb-0 block text-base font-semibold leading-[16px] tracking-[-5%] text-black no-underline hover:underline"
          >
            {name}
          </a>
          {scent && (
            <p className="mb-1 mt-0.5 inline-flex items-center gap-1 rounded-full bg-[#F5F7FA] py-1 pl-1 pr-2 text-xs text-black">
              {scentIconUrl && (
                <span
                  className={`inline-block h-4 w-4 shrink-0 rounded-full ${
                    scentBgColor || ""
                  }`}
                  aria-hidden="true"
                >
                  <span
                    className="block h-full w-full"
                    style={{
                      mask: `url(${scentIconUrl}) no-repeat center`,
                      WebkitMask: `url(${scentIconUrl}) no-repeat center`,
                      backgroundColor: "hsl(var(--color-gray-700))",
                    }}
                  />
                </span>
              )}
              {scent}
            </p>
          )}
          <p className="mb-0 mt-0.5 text-sm text-gray-600">
            <span>{size}</span>
            <span className="hidden md:inline"> / {price}</span>
          </p>
        </div>
      </div>
      <button
        onClick={handleAdd}
        className="flex w-full shrink-0 items-center justify-center rounded-full border-2 border-solid border-black bg-transparent px-[14px] py-1.5 text-xs font-semibold tracking-[10%] text-black transition-colors hover:border-primary-800 hover:bg-primary-800 hover:text-white md:w-auto"
      >
        <span>ADD</span>
        <span className="mx-2 block h-3 w-[2px] bg-current md:hidden"></span>
        <span className="md:hidden">{price}</span>
      </button>
    </div>
  );
};

type ProductRecommendationsResponse = {
  products?: Array<{
    handle: string;
  }>;
};

export const RecommendedProducts = () => {
  const product = usePdpProduct();

  const complementaryQuery = useQuery({
    queryKey: ["pdp-complementary-recommendation", product.id],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const recommendations = await fetchJson<ProductRecommendationsResponse>(
        `/recommendations/products.json?product_id=${product.id}&limit=1&intent=complementary`
      );
      return recommendations.products?.[0]?.handle ?? null;
    },
  });

  const complementaryProductQuery = useQuery({
    queryKey: [
      "pdp-complementary-recommendation-product",
      complementaryQuery.data ?? "",
    ],
    enabled: !!complementaryQuery.data,
    staleTime: 5 * 60 * 1000,
    queryFn: () => storefrontApi.handle(complementaryQuery.data!),
    select: (data) => droppsProduct(data),
  });

  const firstRecommended = complementaryProductQuery.data;

  if (!firstRecommended) {
    return null;
  }

  return (
    <div>
      <p className="mb-[10px] text-xs font-medium text-black">Popular Add-on</p>
      <RecommendedProductCard product={firstRecommended} />
    </div>
  );
};

export default RecommendedProducts;
